/**
 * Format checks for free-text payer/basic+ answers against the question's CMM `validationRule`.
 * The answer field and the send-to-prescriber gate both use these, so what blocks sending is
 * always what the field shows — never an AI `validationError` left over from an earlier run.
 */
import { isoToMmddyyyy, mmddyyyyToIso } from "@/utils/dateUtils";
export const FALLBACK_FORMAT_HINT = "Answer doesn't match the expected format.";
const FREE_TEXT_NODE_TYPES = new Set(["text", "textarea", "date", "number"]);
/** True when a non-empty value fails the rule; empty values and unparseable rules never fail. */
export function failsValidationRule(rule, value) {
    const text = String(value ?? "");
    if (!rule || !text.trim())
        return false;
    try {
        return !new RegExp(rule).test(text);
    }
    catch {
        return false;
    }
}
/**
 * The error for a stored date the native date input can't show (it renders it as an empty field), or
 * null. Example: "Please see attached notes" -> `Not a date: "Please see attached notes". Pick a date.`
 */
export function notADateError(value) {
    const text = String(value ?? "").trim();
    return text && !mmddyyyyToIso(text) ? `Not a date: "${text}". Pick a date.` : null;
}
/**
 * True when a free-text question has a non-empty answer that fails its validation rule, or a date
 * question holds text that isn't a date. Dates are checked as MM/DD/YYYY — the form the field shows
 * after coercing older ISO saves.
 */
export function hasAnswerFormatError(questions, checklist) {
    const byId = new Map(questions.map((q) => [q.requirementRuleName, q]));
    return checklist.some((answer) => {
        const question = byId.get(answer.id);
        const nodeType = question?.requirementOptions?.[0]?.nodeType ?? "";
        if (!question || !FREE_TEXT_NODE_TYPES.has(nodeType))
            return false;
        return (answer.options ?? []).some((option) => {
            if (nodeType === "date" && notADateError(option.label))
                return true;
            const value = nodeType === "date" ? isoToMmddyyyy(option.label ?? null) : option.label;
            return failsValidationRule(question.validationRule, value);
        });
    });
}
